import { useAppForm } from '@sparklayer-io/spark-dash-core/lib/app-form';
import { ApiResponseError } from '@sparklayer-io/spark-dash-core/types/errors';
import { t } from 'i18next';
import { useState } from 'react';
import type { I18nPrefix } from '../../../i18n/prefix';
import type { DashboardPageProps } from '../../../lib/dashboard-page';
import { AuthAlert } from '../components/auth-alert';
import { AuthButton } from '../components/auth-button';
import { AuthLayout } from '../components/auth-layout';
import { AuthLogo } from '../components/auth-logo';
import { ReturnToLoginLink } from '../components/return-to-login-link';
import { TextInput } from '../components/text-input';
import { internalPath } from '../lib/internal-path';

const PAGE = 'features:auth.pages.two_factor_challenge_page' satisfies I18nPrefix;

export function TwoFactorChallengePage({ handlers }: DashboardPageProps) {
  const { apiHandler, authHandler, navigationHandler } = handlers;
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showStartAgainLink, setShowStartAgainLink] = useState<boolean>(false);
  const [usingRecoveryCode, setUsingRecoveryCode] = useState<boolean>(false);

  const form = useAppForm({
    defaultValues: { code: '', recoveryCode: '' },
    onSubmit: async ({ value }) => {
      setError(null);
      setShowStartAgainLink(false);
      setLoading(true);
      try {
        const payload = usingRecoveryCode
          ? { recovery_code: value.recoveryCode }
          : { code: value.code };

        await apiHandler.fetch({ method: 'GET', path: '/sanctum/csrf-cookie', maxRedirects: 0 });
        await apiHandler.fetch({ method: 'POST', path: '/two-factor-challenge', payload });
        await authHandler.reloadUser();

        navigationHandler.navigate(internalPath(navigationHandler.query().from) ?? '/', {
          excludeSiteId: true,
          excludeSiteEnv: true,
        });
      } catch (err) {
        if (err instanceof ApiResponseError && err.status === 422) {
          setError(t(`${PAGE}.invalid_code_error`));
          setShowStartAgainLink(true);
        } else if (err instanceof ApiResponseError && err.status === 429) {
          setError(t(`${PAGE}.rate_limited_error`));
        } else {
          console.error(err);
          setError(t('errors:generic'));
        }
      }
      setLoading(false);
    },
  });

  function toggleRecoveryCode() {
    setUsingRecoveryCode((current) => !current);
    setError(null);
    setShowStartAgainLink(false);
    form.reset();
  }

  return (
    <AuthLayout>
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <AuthLogo />
        <div className="mt-8 rounded-lg bg-white p-10 shadow-xl sm:p-14">
          <div>
            <h2 className="mb-4 font-bold text-gray-900 text-xl sm:text-2xl">
              {t(`${PAGE}.heading`)}
            </h2>
            <p className="max-w mt-2 text-gray-600 text-sm">
              {usingRecoveryCode
                ? t(`${PAGE}.description_recovery`)
                : t(`${PAGE}.description_code`)}
            </p>
          </div>

          <div className="mt-6">
            <form
              className="space-y-6"
              onSubmit={(event) => {
                event.preventDefault();
                void form.handleSubmit();
              }}
            >
              {error && (
                <AuthAlert type="error">
                  {error}
                  {showStartAgainLink && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="cursor-pointer font-medium underline"
                        onClick={() =>
                          navigationHandler.navigate('/login', {
                            excludeSiteId: true,
                            excludeSiteEnv: true,
                          })
                        }
                      >
                        {t(`${PAGE}.start_again`)}
                      </button>
                    </>
                  )}
                </AuthAlert>
              )}

              {usingRecoveryCode ? (
                <form.Field name="recoveryCode">
                  {(field) => (
                    <TextInput
                      id="recovery_code"
                      name="recovery_code"
                      label={t(`${PAGE}.recovery_code_label`)}
                      autoComplete="off"
                      required
                      autoFocus
                      placeholder={t(`${PAGE}.recovery_code_placeholder`)}
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  )}
                </form.Field>
              ) : (
                <form.Field name="code">
                  {(field) => (
                    <TextInput
                      id="code"
                      name="code"
                      label={t(`${PAGE}.code_label`)}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      required
                      autoFocus
                      placeholder={t(`${PAGE}.code_placeholder`)}
                      value={field.state.value}
                      onChange={field.handleChange}
                    />
                  )}
                </form.Field>
              )}

              <div>
                <AuthButton loading={loading}>{t(`${PAGE}.submit`)}</AuthButton>
              </div>

              <div className="text-center text-sm">
                <button
                  type="button"
                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500"
                  onClick={toggleRecoveryCode}
                >
                  {usingRecoveryCode
                    ? t(`${PAGE}.use_authentication_code`)
                    : t(`${PAGE}.use_recovery_code`)}
                </button>
              </div>
            </form>
          </div>
        </div>
        <ReturnToLoginLink
          navigationHandler={navigationHandler}
          className="max-w pt-10 text-gray-600 text-sm"
        />
      </div>
    </AuthLayout>
  );
}
